/**
 * notify.ts — centralised notification utility
 *
 * Wraps Ant Design's `notification` API so every toast across the app
 * uses consistent placement, duration, and styling.
 *
 * Usage:
 *   import { notify } from '../utils/notify';
 *   notify.success('Saved!');
 *   notify.error('Something went wrong', 'Could not connect to the server.');
 *   notify.warn('Heads up', 'Some rows were skipped.');
 *   notify.csvImportResult({ matched: 5, unmatched: ['FOO'], wrongModel: ['BAR'] });
 */

import { notification } from 'antd';

const DURATION_SHORT  = 3;  // seconds
const DURATION_NORMAL = 5;
const DURATION_LONG   = 8;

export const notify = {
  success(title: string, description?: string) {
    notification.success({
      message:     title,
      description,
      placement:   'topRight',
      duration:    DURATION_SHORT,
    });
  },

  error(title: string, description?: string) {
    notification.error({
      message:     title,
      description,
      placement:   'topRight',
      duration:    DURATION_LONG,
    });
  },

  warn(title: string, description?: string) {
    notification.warning({
      message:     title,
      description,
      placement:   'topRight',
      duration:    DURATION_NORMAL,
    });
  },

  info(title: string, description?: string) {
    notification.info({
      message:     title,
      description,
      placement:   'topRight',
      duration:    DURATION_NORMAL,
    });
  },

  /**
   * Shows a structured result notification after a CSV import.
   *
   * @param matched      - number of SKUs successfully matched & selected
   * @param unmatched    - SKU names from CSV that were not found in the system
   * @param wrongModel   - SKU names that exist but have the wrong buying model
   *                       for the selected conversion type
   */
  csvImportResult({
    matched,
    unmatched,
    wrongModel,
  }: {
    matched:    number;
    unmatched:  string[];
    wrongModel: string[];
  }) {
    const hasErrors = unmatched.length > 0 || wrongModel.length > 0;

    if (matched === 0 && !hasErrors) {
      notification.warning({
        message:     'Nothing Imported',
        description: 'No rows found in the CSV. Make sure it has sku_name and target_sku columns.',
        placement:   'topRight',
        duration:    DURATION_NORMAL,
      });
      return;
    }

    if (matched === 0) {
      // Everything failed
      const lines: string[] = [];
      if (wrongModel.length) {
        lines.push(`Wrong buying model (${wrongModel.length}): ${cap(wrongModel, 3)}`);
      }
      if (unmatched.length) {
        lines.push(`Not found (${unmatched.length}): ${cap(unmatched, 3)}`);
      }
      notification.error({
        message:     'CSV Import Failed',
        description: lines.join('\n'),
        placement:   'topRight',
        duration:    DURATION_LONG,
      });
      return;
    }

    // At least some matched — show success, but warn about issues
    if (hasErrors) {
      const lines: string[] = [
        `${matched} SKU${matched !== 1 ? 's' : ''} imported successfully.`,
      ];
      if (wrongModel.length) {
        lines.push(
          `⚠️  ${wrongModel.length} SKU${wrongModel.length !== 1 ? 's' : ''} skipped — wrong buying model for this conversion: ${cap(wrongModel, 3)}`,
        );
      }
      if (unmatched.length) {
        lines.push(
          `⚠️  ${unmatched.length} SKU${unmatched.length !== 1 ? 's' : ''} not found in system: ${cap(unmatched, 3)}`,
        );
      }
      notification.warning({
        message:     'CSV Imported with Issues',
        description: lines.join('\n'),
        placement:   'topRight',
        duration:    DURATION_LONG,
      });
    } else {
      notification.success({
        message:     'CSV Imported',
        description: `${matched} SKU${matched !== 1 ? 's' : ''} selected and target names pre-filled.`,
        placement:   'topRight',
        duration:    DURATION_SHORT,
      });
    }
  },
};

// ── helpers ───────────────────────────────────────────────────────────────────

/** Return up to `max` items joined by comma, with '…' if there are more. */
function cap(arr: string[], max: number): string {
  if (arr.length <= max) { return arr.join(', '); }
  return `${arr.slice(0, max).join(', ')} +${arr.length - max} more`;
}
